import { Component, OnInit, ViewEncapsulation, Inject, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { Validators, UntypedFormGroup, FormGroup, FormControl } from '@angular/forms';

import { combineLatest, noop, Observable, throwError, filter, catchError, tap } from 'rxjs';
import { TsSnackbarService } from 'tsui';
import { Select, Store } from '@ngxs/store';
import { Pipelines } from '@state/pipelines/action';
import { PipelinesState } from '@state/pipelines/state';
import { ProjectsService } from '../../projects.service';
import { Project, Pipeline, ProjectTemplate, SaveProjectMode } from 'app/modules/projects/projects.types';

export interface ProjectEditPipelineData {
  project: Project;
  pipelines: Pipeline[];
  data: any;
}

interface ProjectUpdatePayload {
  id: string;
  pipeline_id: number;
  template_id?: number;
}

@Component({
  selector: 'project-edit-pipepline',
  templateUrl: './project-edit-pipeline.component.html',
  styleUrls: ['./project-edit-pipeline.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ProjectEditPipelineComponent implements OnInit {
  addProjectForm: UntypedFormGroup;
  project: Project;
  projects: Project[];
  pipelines: Pipeline[];
  viewGroups: any[];
  currentPipeline: Pipeline;
  projectTemplates: ProjectTemplate<any>[];
  public showChangeTemplateOption: boolean = true;

  private store = inject(Store);

  @Select(PipelinesState.getPipelines) pipelines$: Observable<Pipeline[]>;
  @Select(PipelinesState.getPipelinesGroups) pipelineGroups$: Observable<any[]>;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public matDialogRef: MatDialogRef<ProjectEditPipelineComponent>,
    private _projectsService: ProjectsService,
    private readonly snackBar: TsSnackbarService,
  ) {
    this.addProjectForm = new FormGroup({
      pipeline_id: new FormControl('', [Validators.required]),
      template_id: new FormControl(''),
    });
  }

  ngOnInit(): void {
    this.currentPipeline = this.data.currentPipeline;

    this.store.dispatch([Pipelines.Get, Pipelines.GetGroups]);

    combineLatest([this.pipelines$, this.pipelineGroups$])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(([pipelines, groups]) => {
          return Array.isArray(pipelines) && Array.isArray(groups);
        }),
      )
      .subscribe(([pipelines, groups]) => {
        this.pipelines = pipelines;
        this.viewGroups = groups;
        const orderedGroups = groups.sort((group1, group2) => (group1.rank > group2.rank ? 1 : -1));
        const orderedPipelines = [];
        orderedGroups.forEach((gItem) => {
          const memberIds = gItem.members.map((i) => i.groupable_id);
          pipelines.forEach((pItem) => {
            if (memberIds.indexOf(pItem.id) >= 0) {
              orderedPipelines.push({ ...pItem, name: `${gItem.label}: ${pItem.name}` });
            }
          });
        });
        //Push the pipelines that don't belong to group
        pipelines.forEach((pItem) => {
          if (!pItem.object_group_id) {
            orderedPipelines.push(pItem);
          }
        });
        this.pipelines = orderedPipelines.filter(
          (pipeline) => pipeline?.pipeline_type !== 'dynamic' && pipeline.id !== this.data.currentPipeline.id,
        );
      });

    if (this.data.project) {
      this.project = this.data.project;
      if (this.data.project.template) {
        this.addProjectForm.get('template_id').setValue(this.data.project.template?.id);
      }
    } else if (this.data.projects) {
      this.projects = this.data.projects;
    }
    if (this.project) {
      this._projectsService
        .getProjectById(this.project.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((p) => {
          this.addProjectForm.get('template_id').setValue(p.template?.id);
        });
    }

    if (this.data.hideTemplateOption) {
      this.showChangeTemplateOption = !this.data.hideTemplateOption;
    }
    this.projectTemplates = this.data.data.options.project_templates
      .filter((option) => !option.hidden && option.related_to_type == 'Project')
      .sort((a, b) => a.rank - b.rank);
  }

  discard(): void {
    this.matDialogRef.close({ moved: false });
  }

  save(): void {
    this.addProjectForm.disable();

    const projectForm = this.addProjectForm.value;

    if (this.project) {
      const updatePayload: ProjectUpdatePayload = {
        id: this.project.id,
        pipeline_id: projectForm.pipeline_id,
      };

      if (projectForm.template_id) {
        updatePayload.template_id = projectForm.template_id;
      }

      this._projectsService
        .saveProject(updatePayload as Project)
        .pipe(takeUntilDestroyed(this.destroyRef), this.movingDealCatchError)
        .subscribe((project) => {
          this.matDialogRef.close({ moved: true, project });
        });
    } else if (this.projects) {
      const projectsToUpdate = this.projects.map((project) => ({
        id: project.id,
        pipeline_id: projectForm.pipeline_id,
        template_id: projectForm.template_id ? projectForm.template_id : project.template_id,
      }));

      this._projectsService
        .saveProjects(projectsToUpdate as Project[], SaveProjectMode.MovePipeline)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          tap((projects) => this.matDialogRef.close({ updated: true, projects })),
          this.movingDealCatchError,
        )
        .subscribe(noop);
    }
  }

  private movingDealCatchError = catchError((error) => {
    const fallbackMessage = `Deal status doesn't exist on the new view. Please change the status and try again.`;
    this.addProjectForm.enable();
    const errorMessage = error?.error?.project?.errors?.join('. ') || fallbackMessage;
    this.snackBar.open(errorMessage, 'error');
    return throwError(() => new Error(errorMessage));
  });
}
